import {
  Filter,
  FilterContext,
  FilterOption,
  SavedFilter,
} from 'app/modules/filters/models';

import { hasPermissionChecker } from 'app/modules/permissions/utils';
import { isEqual } from 'lodash';
import {
  selectFeatureFlags,
  selectSavedFiltersEnabled,
} from 'app/shared/featureFlags/selectors';
import { selectSessionAgentPermissions } from 'app/modules/session/selectors';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import styled from 'styled-components';

import { FilterBuilder } from 'app/modules/filters/components/FilterBuilder';
import { FilterAppliedChip } from 'app/modules/filters/components/FilterAppliedChip';
import { FilterPinButton } from 'app/modules/filters/components/FilterPinButton';
import { FilterPinnedChip } from 'app/modules/filters/components/FilterPinnedChip';
import { SaveFilterButton } from 'app/modules/filters/components/SaveFilterButton';
import { SavedFilterPanel } from 'app/modules/filters/components/SavedFilterPanel';
import { SavedFiltersMenuButton } from 'app/modules/filters/components/SavedFiltersMenuButton';
import { useGetSavedFilters } from 'app/modules/filters/queries/useGetSavedFilters';
import { getValidFilters } from 'app/modules/filters/utils';
import { IconChevronDown, IconChevronUp, IconFilter } from '@u21/tabler-icons';
import {
  U21Button,
  U21Divider,
  U21MenuLayout,
  U21MenuList,
  U21MenuListOptionProps,
  U21Spacer,
  U21Tabs,
} from 'app/shared/u21-ui/components';
import { selectDataSettingsByNativeKey } from 'app/modules/dataSettings/selectors';
import { keyPathToLabel } from 'app/modules/dataSettings/utils';

interface Props {
  defaultFilters?: Filter[];
  disabled?: boolean;
  filters: Filter[];
  filterContext?: FilterContext;
  onChange: (filters: Filter[]) => void;
  onPinChange?: (pinned: string[]) => void;
  options: FilterOption[];
  pinned?: string[];
}

enum FilterTab {
  NEW = 'NEW',
  SAVED = 'SAVED',
}

const DEFAULT_FILTERS = [];
const DEFAULT_PINNED = [];

export const Filters = (props: Props) => {
  const {
    defaultFilters = DEFAULT_FILTERS,
    disabled,
    filters,
    filterContext,
    onChange,
    onPinChange,
    options: optionsProp,
    pinned = DEFAULT_PINNED,
  } = props;
  const [open, setOpen] = useState(false);
  const [selectedFilterKey, setSelectedFilterKey] = useState<string>();
  const [activeTab, setActiveTab] = useState<FilterTab>(FilterTab.NEW);
  const [appliedSavedFilter, setAppliedSavedFilter] = useState<SavedFilter>();

  const featureFlags = useSelector(selectFeatureFlags);
  const userPermissions = useSelector(selectSessionAgentPermissions);
  const dataSettingsByKey = useSelector(selectDataSettingsByNativeKey);
  const savedFiltersEnabled = useSelector(selectSavedFiltersEnabled);

  const { data: savedFilters = [] } = useGetSavedFilters(filterContext, {
    enabled: Boolean(filterContext) && savedFiltersEnabled,
  });

  const options = useMemo(
    () =>
      optionsProp.map((i) => {
        const { key, unit21DataClassifier } = i;
        if (unit21DataClassifier) {
          const dataSetting = dataSettingsByKey[unit21DataClassifier][key];
          if (dataSetting) {
            return {
              ...i,
              description: dataSetting.description,
              label: keyPathToLabel(dataSetting),
            };
          }
        }
        return i;
      }),
    [dataSettingsByKey, optionsProp],
  );

  const optionMap = useMemo(
    () =>
      options.reduce<Record<FilterOption['key'], FilterOption>>(
        (acc, i) => ({ ...acc, [i.key]: i }),
        {},
      ),
    [options],
  );

  const allowedOptions = useMemo(
    () =>
      options.filter((i) => {
        if (i.featureFlag && !featureFlags[i.featureFlag]) {
          return false;
        }
        return hasPermissionChecker(userPermissions, i.permissions);
      }),
    [featureFlags, options, userPermissions],
  );

  const resolveSavedFilters = useCallback(
    (saved: Filter[]): Filter[] => {
      const valid = getValidFilters(saved, allowedOptions);
      const validKeys = new Set(valid.map((i) => i.key));
      const missingRequired = defaultFilters.filter(
        (i) => optionMap[i.key]?.required && !validKeys.has(i.key),
      );
      return [...missingRequired, ...valid];
    },
    [allowedOptions, defaultFilters, optionMap],
  );

  const appliedSavedFilters = useMemo(
    () =>
      savedFilters.filter((i) =>
        isEqual(resolveSavedFilters(i.filters), filters),
      ),
    [savedFilters, filters, resolveSavedFilters],
  );

  const appliedSavedFilterIds = useMemo(
    () => new Set(appliedSavedFilters.map((i) => i.id)),
    [appliedSavedFilters],
  );

  // when no applied state set + current filters match a saved filter, lock onto the first match
  const appliedSavedFilterRef = useRef(appliedSavedFilter);
  useEffect(() => {
    appliedSavedFilterRef.current = appliedSavedFilter;
  }, [appliedSavedFilter]);
  useEffect(() => {
    if (
      savedFiltersEnabled &&
      !appliedSavedFilterRef.current &&
      appliedSavedFilters.length > 0
    ) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setAppliedSavedFilter(appliedSavedFilters[0]);
    }
  }, [appliedSavedFilters, savedFiltersEnabled]);

  const staleSavedFilterIds = useMemo(
    () =>
      new Set(
        savedFilters
          .filter(
            (i) =>
              getValidFilters(i.filters, allowedOptions).length <
              i.filters.length,
          )
          .map((i) => i.id),
      ),
    [savedFilters, allowedOptions],
  );

  // filter out invalid pinned keys
  const pinnedSet = useMemo(
    () => new Set(pinned.filter((i) => optionMap[i])),
    [optionMap, pinned],
  );

  const filterKeyMap = useMemo(
    () =>
      filters.reduce(
        (acc, i) => ({
          ...acc,
          [i.key]: i,
        }),
        {},
      ),
    [filters],
  );

  const onPinChangeWrapper = useCallback(
    (key: string) =>
      onPinChange
        ? () => {
            if (pinnedSet.has(key)) {
              onPinChange?.([...pinnedSet].filter((j) => j !== key));
            } else {
              onPinChange?.([...pinnedSet, key]);
            }
          }
        : undefined,
    [onPinChange, pinnedSet],
  );

  const filterOptions = useMemo(() => {
    return allowedOptions.reduce<U21MenuListOptionProps[]>((acc, i) => {
      const { label, key, description } = i;
      if (filterKeyMap[key]) {
        return acc;
      }
      acc.push({
        description,
        onClick: () => setSelectedFilterKey(key),
        rightIcon: (
          <FilterPinButton
            onPinChange={onPinChangeWrapper(key)}
            pinned={pinnedSet.has(key)}
          />
        ),
        text: label,
        typographyProps: { ellipsis: true },
      });
      return acc;
    }, []);
  }, [allowedOptions, filterKeyMap, onPinChangeWrapper, pinnedSet]);

  const chipFilterKeys = useMemo(
    () => [...new Set([...pinnedSet, ...Object.keys(filterKeyMap)])],
    [filterKeyMap, pinnedSet],
  );

  const onChangeRef = useRef(onChange);
  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  // remove filters with no options
  useEffect(() => {
    const newFilters = filters.filter((i) => optionMap[i.key]);
    if (newFilters.length < filters.length) {
      onChangeRef.current(newFilters);
    }
  }, [filters, optionMap]);

  // warn if required filter doesn't exist on dev
  useEffect(() => {
    if (process.env.NODE_ENV !== 'production') {
      const defaultFiltersKeys = new Set(defaultFilters.map((i) => i.key));
      const missingDefaultFilters = options.filter((i) => {
        return i.required && !defaultFiltersKeys.has(i.key);
      });
      if (missingDefaultFilters.length) {
        throw new Error(
          `Missing default filters for required options: ${new Intl.ListFormat('en-US').format(missingDefaultFilters.map((i) => i.key))}`,
        );
      }
    }
  }, [defaultFilters, options]);

  const onApplySavedFilter = useCallback(
    (savedFilter: SavedFilter) => {
      onChange(resolveSavedFilters(savedFilter.filters));
      setAppliedSavedFilter(savedFilter);
      setOpen(false);
    },
    [onChange, resolveSavedFilters],
  );

  return (
    <U21Spacer>
      {savedFiltersEnabled && filterContext && (
        <SavedFiltersSpace>
          <SavedFiltersMenuButton
            appliedSavedFilter={appliedSavedFilter}
            appliedSavedFilterIds={appliedSavedFilterIds}
            filterContext={filterContext}
            onApply={onApplySavedFilter}
            onAppliedSavedFilterChange={setAppliedSavedFilter}
            savedFilters={savedFilters}
            staleSavedFilterIds={staleSavedFilterIds}
          />
        </SavedFiltersSpace>
      )}
      <U21Spacer wrap>
        <U21MenuLayout
          onClose={() => setOpen(false)}
          onExited={() => setSelectedFilterKey(undefined)}
          open={open}
          trigger={
            <U21Button
              color="primary"
              disabled={disabled}
              endIcon={open ? <IconChevronUp /> : <IconChevronDown />}
              onClick={() => setOpen(true)}
              size="small"
              startIcon={<IconFilter />}
            >
              Filter
            </U21Button>
          }
        >
          <MenuContent>
            {selectedFilterKey ? (
              <FilterBuilder
                onClose={() => setOpen(false)}
                onPinChange={onPinChangeWrapper(selectedFilterKey)}
                onReset={() => setSelectedFilterKey(undefined)}
                onSubmit={(operatorAndValue) => {
                  const newFilter = {
                    ...operatorAndValue,
                    key: selectedFilterKey,
                  };
                  onChange([...filters, newFilter]);
                  setOpen(false);
                }}
                option={optionMap[selectedFilterKey]}
                pinned={pinnedSet.has(selectedFilterKey)}
              />
            ) : (
              <U21Spacer>
                {savedFiltersEnabled && filterContext && (
                  <U21Tabs
                    onTabChange={(value) => setActiveTab(value)}
                    tabs={[
                      { label: 'New Filter', value: FilterTab.NEW },
                      { label: 'Saved Filters', value: FilterTab.SAVED },
                    ]}
                    value={activeTab}
                  />
                )}
                {savedFiltersEnabled &&
                filterContext &&
                activeTab === FilterTab.SAVED ? (
                  <SavedFilterPanel
                    appliedSavedFilterIds={appliedSavedFilterIds}
                    filterContext={filterContext}
                    onApply={onApplySavedFilter}
                    onDeleted={(savedFilter) => {
                      if (appliedSavedFilter?.id === savedFilter.id) {
                        setAppliedSavedFilter(undefined);
                      }
                    }}
                    savedFilters={savedFilters}
                    staleSavedFilterIds={staleSavedFilterIds}
                  />
                ) : (
                  <U21MenuList options={filterOptions} />
                )}
                <U21Divider horizontal />
                <FooterContainer>
                  {!isEqual(defaultFilters, filters) && (
                    <U21Button
                      onClick={() => {
                        onChange(defaultFilters);
                        setAppliedSavedFilter(undefined);
                        setOpen(false);
                      }}
                      size="small"
                      variant="ghost"
                    >
                      Reset Filters
                    </U21Button>
                  )}
                  <StyledU21Button onClick={() => setOpen(false)} size="small">
                    Cancel
                  </StyledU21Button>
                </FooterContainer>
              </U21Spacer>
            )}
          </MenuContent>
        </U21MenuLayout>
        {chipFilterKeys.map((i) => {
          const option = optionMap[i];
          // ignore if option doesn't exist. will be removed w/ a useEffect
          if (!option) {
            return null;
          }
          if (filterKeyMap[i]) {
            const { key, ...value } = filterKeyMap[i];
            return (
              <FilterAppliedChip
                disabled={disabled}
                key={key}
                onDelete={() => onChange(filters.filter((j) => j.key !== key))}
                onUpdate={(updatedFilter) =>
                  onChange(
                    filters.map((j) =>
                      j.key === key ? { ...j, ...updatedFilter } : j,
                    ),
                  )
                }
                operatorAndValue={value}
                option={option}
                pinned={pinnedSet.has(i)}
                onPinChange={onPinChangeWrapper(i)}
              />
            );
          }
          return (
            <FilterPinnedChip
              disabled={disabled}
              key={i}
              onSubmit={(operatorAndValue) => {
                const newFilter = {
                  ...operatorAndValue,
                  key: i,
                };
                onChange([...filters, newFilter]);
              }}
              onPinChange={onPinChangeWrapper(i)}
              option={option}
            />
          );
        })}
        {savedFiltersEnabled && filterContext && (
          <RightActions>
            <SaveFilterButton
              appliedSavedFilter={appliedSavedFilter}
              defaultFilters={defaultFilters}
              duplicateSavedFilter={appliedSavedFilters[0]}
              filterContext={filterContext}
              filters={filters}
              onAppliedSavedFilterChange={setAppliedSavedFilter}
            />
          </RightActions>
        )}
      </U21Spacer>
    </U21Spacer>
  );
};

const SavedFiltersSpace = styled.div`
  display: flex;
`;

const RightActions = styled.div`
  margin-left: auto;
`;

const MenuContent = styled.div`
  min-width: 300px;
  max-width: 600px;
`;

const FooterContainer = styled.div`
  display: flex;
`;

const StyledU21Button = styled(U21Button)`
  margin-left: auto;
`;
